import React, { useState, useEffect } from 'react';
import { getSettings, updateSettings, uploadFile, UPLOAD_URL, getMediaUrl } from '../../services/api';

const AdminSettings = () => {
    const [settings, setSettings] = useState({
        signature: '',
        gemini_api_key: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
    };

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const result = await getSettings();
                if (result.success) {
                    setSettings(prev => ({ ...prev, ...result.data }));
                }
            } catch (error) {
                console.error("Error fetching settings:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const handleSignatureUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setSaving(true);
            const uploadRes = await uploadFile(file);
            if (uploadRes.success) {
                const newSignature = uploadRes.filename;
                const updateRes = await updateSettings({ signature: newSignature });
                if (updateRes.success) {
                    setSettings(prev => ({ ...prev, signature: newSignature }));
                    showToast('Signature updated successfully');
                }
            }
        } catch (error) {
            console.error("Error uploading signature:", error);
            showToast('Failed to upload signature', 'error');
        } finally {
            setSettings(prev => ({ ...prev })); // Ensure state sync
            setSaving(false);
        }
    };

    const handleSaveGeminiKey = async () => {
        try {
            setSaving(true);
            const updateRes = await updateSettings({ gemini_api_key: settings.gemini_api_key });
            if (updateRes.success) {
                showToast('Gemini API Key updated successfully');
            }
        } catch (error) {
            console.error("Error saving Gemini API Key:", error);
            showToast('Failed to save API Key', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-8 flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
            </div>
        );
    }

    return (
        <div className="p-8 max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-800 mb-8 flex items-center gap-3">
                <i className="fas fa-cog text-emerald-600"></i>
                Platform Settings
            </h1>



            <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-gray-50">
                    <h2 className="text-xl font-bold text-gray-800">Certificate Settings</h2>
                    <p className="text-gray-500 text-sm">Configure how certificates look for your students</p>
                </div>

                <div className="p-8 space-y-8">
                    {/* Signature Upload */}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-4">Authorized Signature</label>
                        <div className="flex flex-col md:flex-row items-start gap-8">
                            <div className="w-full md:w-64 h-40 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center overflow-hidden relative group">
                                {settings.signature ? (
                                    <>
                                        <img 
                                            src={getMediaUrl(settings.signature)} 
                                            alt="Admin Signature" 
                                            className="w-full h-full object-contain p-4" 
                                        />
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <p className="text-white text-xs font-bold">Click Change below</p>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center p-4">
                                        <i className="fas fa-signature text-4xl text-gray-300 mb-2"></i>
                                        <p className="text-gray-400 text-xs">No signature uploaded</p>
                                    </div>
                                )}
                            </div>

                            <div className="flex-1 space-y-4">
                                <p className="text-gray-600 text-sm leading-relaxed">
                                    Upload a high-quality PNG or SVG image of the authorized signature. 
                                    This signature will appear on all completion certificates.
                                    Recommended size: 300x150 pixels with transparent background.
                                </p>
                                
                                <div className="flex items-center gap-3">
                                    <label className={`
                                        cursor-pointer px-6 py-2.5 rounded-xl font-bold text-sm transition-all
                                        ${saving ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg shadow-emerald-600/20'}
                                    `}>
                                        {saving ? 'Processing...' : settings.signature ? 'Change Signature' : 'Upload Signature'}
                                        <input 
                                            type="file" 
                                            className="hidden" 
                                            accept="image/*" 
                                            onChange={handleSignatureUpload}
                                            disabled={saving}
                                        />
                                    </label>
                                    {settings.signature && (
                                        <button 
                                            onClick={() => setSettings(prev => ({ ...prev, signature: '' }))}
                                            className="text-red-500 hover:text-red-700 font-bold text-sm"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <hr className="border-gray-100" />

                    {/* Google Gemini API Key */}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Google Gemini API Key</label>
                        <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                            Configure the Google Gemini API Key to enable automatic generation of course exams based on topics and lessons. 
                            If the key is saved, it will display as placeholder text. Leave it unchanged to keep the existing key.
                        </p>
                        <div className="flex gap-3 max-w-xl">
                            <input
                                type="password"
                                value={settings.gemini_api_key || ''}
                                onChange={(e) => setSettings(prev => ({ ...prev, gemini_api_key: e.target.value }))}
                                placeholder="Enter Google Gemini API Key"
                                className="flex-1 px-4 py-3 border-2 border-gray-100 focus:border-emerald-500 rounded-xl outline-none text-sm transition-all"
                            />
                            <button
                                onClick={handleSaveGeminiKey}
                                disabled={saving}
                                className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap
                                    ${saving ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg shadow-emerald-600/20'}
                                `}
                            >
                                {saving ? 'Saving...' : 'Save Key'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Toast Notification */}
            {toast.show && (
                <div className="fixed bottom-6 right-6 z-[2000] animate-fadeIn">
                    <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl border-l-4 min-w-[300px] ${
                        toast.type === 'success' ? 'bg-white border-emerald-600' : 'bg-white border-red-600'
                    }`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            toast.type === 'success' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
                        }`}>
                            <i className={`fas ${toast.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                        </div>
                        <p className="text-gray-800 text-sm font-medium">{toast.message}</p>
                    </div>
                </div>
            )}

            <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-out;
                }
            `}</style>
        </div>
    );
};

export default AdminSettings;
